import { LoadingOutlined, RightCircleOutlined } from '@ant-design/icons';
import * as S from '@unionfab/ufc-shop-commons';
import { i18nFormat, getUfcShopComposedApi } from '@unionfab/ufc-shop-commons';
import { Popover, Steps } from 'antd';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';

import { StepTimeKey, useOrderDetailsStore } from '@/stores';

import styles from './index.module.less';
import { OrderCurrentStep } from './OrderCurrentStep';
import {
  getUseShopQuoteStoreByCode,
  useShopExpressStore,
  loadOrderInShopEditorStore,
} from '@/stores';
const { Step } = Steps;

const shortFormatTemp = 'MMM D';
const formatTemp = 'dddd, MMM. Do';

interface OrderEstDeliveryAndStepsProps {
  orderCode: string;
}

export const OrderEstDeliveryAndSteps = ({
  orderCode,
}: OrderEstDeliveryAndStepsProps) => {
  const { orderStepTimeMap, loadingOrder, order } = useOrderDetailsStore();
  const useShopQuoteStore = getUseShopQuoteStoreByCode(orderCode);
  useEffect(() => {
    const loadData = async () => {
      if (order) {
        await loadOrderInShopEditorStore(orderCode);
        await useShopExpressStore
          .getState()
          .loadShopExpresses(order.deliver.deliverAddress[0].country);
      }
    };

    loadData();
  }, [orderCode, order]);

  const { shopOrder } = useShopQuoteStore();
  const { expressList } = useShopExpressStore(s => ({
    expressList: s.shopExpresses,
  }));

  // 草稿/未支付订单：显示预计时间（交期）
  const isDraftOrUnpaid =
    !!order && S.UnpaidInquiryOrderStatusList.includes(order.status);

  // 交期（预计发货日期）改为后端接口计算，仅在草稿/未支付时启用
  const skuKey = (S.get(shopOrder, o => o.items) || [])
    .map(i => i?.skuId || '')
    .join('|');
  const { data: shipmentDateStr } = useQuery({
    enabled: !!orderCode && isDraftOrUnpaid,
    refetchOnWindowFocus: false,
    queryKey: ['calcDelivery', orderCode, skuKey],
    queryFn: () =>
      getUfcShopComposedApi().shopOrderQueryApi.calcDelivery(orderCode),
  });
  const getStepTime = (k: StepTimeKey, temp?: string) => {
    const step = orderStepTimeMap[k];
    if (isNil(step)) {
      return '-';
    }
    // 只要有值就展示：可解析则格式化，否则直接展示原始字符串
    const date = step.value;
    if (!date) return '-';
    if (dayjs(date).isValid()) {
      return dayjs(date).format(temp || shortFormatTemp);
    }
    return date;
  };

  const estSteps = useMemo(() => {
    if (loadingOrder || !S.isValid(orderStepTimeMap)) {
      return <></>;
    }

    const stepKeyDescMap: Partial<Record<StepTimeKey, string>> = {
      payTime: 'Pay Time',
      confirmTime: 'Confirm Order',
      processTime: 'In-Process',
      shipmentTime: 'Shipped',
      deliveryTime: 'Delivered',
    };

    if (!orderStepTimeMap.payTime.value) {
      delete stepKeyDescMap.payTime;
    } else {
      delete stepKeyDescMap.confirmTime;
    }

    let current = 0;

    if (
      typeof orderStepTimeMap.deliveryTime !== 'undefined' &&
      !orderStepTimeMap.deliveryTime.est
    ) {
      current = 3;
    } else if (
      typeof orderStepTimeMap.shipmentTime !== 'undefined' &&
      !orderStepTimeMap.shipmentTime.est
    ) {
      current = 2;
    } else if (
      typeof orderStepTimeMap.processTime !== 'undefined' &&
      !orderStepTimeMap.processTime.est
    ) {
      current = 1;
    } else {
      current = 0;
    }

    return (
      <div className={styles.estSteps}>
        <h4>
          {i18nFormat('The estimated time varies with the time of payment')}
        </h4>
        <Steps progressDot current={current}>
          {Object.keys(stepKeyDescMap).map((k: StepTimeKey) => (
            <Step
              key={k}
              description={
                <div className={styles.stepDesc}>
                  <span>{getStepTime(k)}</span>
                  <span>{i18nFormat(stepKeyDescMap[k])}</span>
                </div>
              }
            />
          ))}
        </Steps>
      </div>
    );
  }, [loadingOrder, orderStepTimeMap]);

  const deliverAt = useMemo(() => {
    let label = 'Delivery';

    const deliveryTime = S.get(orderStepTimeMap, o => o.deliveryTime);

    if (deliveryTime && !isNil(deliveryTime.est) && deliveryTime.est) {
      label = i18nFormat('EST Delivery');
    }

    return (
      <div className={styles.deliveryAt}>
        <span>{label}:</span>
        <span>
          {loadingOrder ? (
            <LoadingOutlined style={{ color: '#FFCE00' }} />
          ) : isDraftOrUnpaid ? (
            S.getShopOrderLeadTimes(shopOrder, expressList, shipmentDateStr)
              ?.estDeliveryStr || '-'
          ) : (
            getStepTime('deliveryTime', formatTemp)
          )}
        </span>
        <Popover content={estSteps} placement="bottom" trigger={'click'}>
          <RightCircleOutlined style={{ color: '#FFCE00' }} />
        </Popover>
      </div>
    );
  }, [
    loadingOrder,
    orderStepTimeMap,
    shopOrder,
    expressList,
    isDraftOrUnpaid,
    shipmentDateStr,
  ]);

  const deliverMethod = useMemo(() => {
    if (loadingOrder || !order) {
      return <></>;
    }
    const deliver = order?.deliver;
    const deliverAddress = S.get(deliver, d => d.deliverAddress[0]);
    const expressCompany = S.get(deliverAddress, da => da.expressCompany, '-');
    const expressNumber = S.get(
      deliverAddress,
      da => da.expressNumber[0],
      'Update after shipped',
    );

    const delivery = shopOrder?.delivery;

    return (
      <div className={styles.deliverMethod}>
        <div>
          <span>{i18nFormat('Shipping Method')}:</span>
          <span>
            {i18nFormat(expressCompany)}
            {delivery && delivery.fedexAccount && ` (${delivery.fedexAccount})`}
          </span>
        </div>
        <div>
          <span>{i18nFormat('Tracking Number')}:</span>
          <span>{i18nFormat(expressNumber)}</span>
        </div>
      </div>
    );
  }, [order, loadingOrder]);

  return (
    <div className={styles.container}>
      {deliverAt}
      <OrderCurrentStep />
      {deliverMethod}
    </div>
  );
};
